'use client';

import { useState, useCallback } from 'react';
import type { IntervalLine } from '@/modules/shared/DataLayers/types';

/**
 * A custom React hook for managing a list of interval lines.
 *
 * This hook provides utilities to:
 * - Add one or more interval lines
 * - Remove a line by its ID
 * - Clear all interval lines
 *
 * use for our scenarios such as range selections, overlays on charts, or marking time windows in sensor data visualizations.
 *
 * @returns An object containing:
 * - `intervalLines`: the current list of interval lines.
 * - `addIntervalLines`: a function to append new interval lines.
 * - `removeIntervalLine`: a function to remove a specific interval line by ID.
 * - `clearIntervalLines`: a function to remove all interval lines.
 */
export const useIntervalLines = () => {
  const [intervalLines, setIntervalLines] = useState<IntervalLine[]>([]);

  /**
   * Adds one or more interval lines to the current list.
   *
   * @param lines - Array of `IntervalLine` objects to be added.
   */
  const addIntervalLines = useCallback((lines: IntervalLine[]) => {
    setIntervalLines((prev) => [...prev, ...lines]);
  }, []);

  /**
   * Removes an interval line by its unique identifier.
   *
   * @param id - The ID of the interval line to be removed.
   */
  const removeIntervalLine = useCallback((id: string) => {
    setIntervalLines((prev) => prev.filter((line) => line.id !== id));
  }, []);

  /**
   * Clears all interval lines from the list.
   */
  const clearIntervalLines = useCallback(() => {
    setIntervalLines([]);
  }, []);

  return {
    intervalLines,
    addIntervalLines,
    removeIntervalLine,
    clearIntervalLines,
  };
};
